/**
 * Product Detail Modal Component
 * Wrapped with React.memo for performance
 */
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Product } from '../../types/menu';
import { getImageUrl } from '../../config/api';

interface ProductModalProps {
    product: Product;
    currency: string;
    isMenuViewOnly: boolean;
    onClose: () => void;
    onAddToCart: () => void;
}

const ProductModal: React.FC<ProductModalProps> = React.memo(({
    product,
    currency,
    isMenuViewOnly,
    onClose,
    onAddToCart
}) => {
    const { t } = useTranslation();
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    const handleImageLoad = useCallback(() => setImageLoaded(true), []);
    const handleImageError = useCallback(() => setImageError(true), []);

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
        }}>
            <div className="glass-dark cart-modal" style={{
                width: '100%',
                maxWidth: '500px',
                borderRadius: '1.5rem',
                overflow: 'hidden'
            }}>
                {/* Image */}
                <div style={{ position: 'relative', width: '100%', height: '300px', background: 'rgba(255, 255, 255, 0.05)' }}>
                    {/* Loading skeleton */}
                    {!imageLoaded && !imageError && product.image_url && (
                        <div style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 1.5s infinite'
                        }} />
                    )}
                    {product.image_url && !imageError ? (
                        <img
                            src={getImageUrl(product.image_url) || ''}
                            alt={product.name}
                            loading="lazy"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                opacity: imageLoaded ? 1 : 0,
                                transition: 'opacity 0.3s ease-in-out'
                            }}
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                        />
                    ) : (
                        <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '5rem'
                        }}>
                            🍽️
                        </div>
                    )}

                    <button
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            position: 'absolute',
                            top: '1rem',
                            right: '1rem',
                            background: 'rgba(0, 0, 0, 0.7)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            color: 'white',
                            fontSize: '1.5rem',
                            cursor: 'pointer'
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div style={{ padding: '1.5rem' }}>
                    <h2 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>
                        {product.name}
                    </h2>

                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6', marginBottom: '1rem' }}>
                        {product.description}
                    </p>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '1.5rem'
                    }}>
                        <span style={{ color: '#FFD700', fontSize: '1.75rem', fontWeight: 'bold' }}>
                            {Math.round(Number(product.price))} {currency}
                        </span>
                        <span style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'rgba(255, 255, 255, 0.7)',
                            padding: '0.5rem 1rem',
                            borderRadius: '0.5rem',
                            fontSize: '0.875rem'
                        }}>
                            {product.category_name}
                        </span>
                    </div>

                    {!isMenuViewOnly ? (
                        <button
                            onClick={onAddToCart}
                            style={{
                                width: '100%',
                                padding: '1rem',
                                borderRadius: '0.75rem',
                                border: 'none',
                                background: 'rgba(139, 0, 0, 0.9)',
                                color: 'white',
                                fontSize: '1.125rem',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.3s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(139, 0, 0, 1)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'rgba(139, 0, 0, 0.9)'}
                        >
                            {t('product.addToCart')}
                        </button>
                    ) : (
                        <div style={{
                            width: '100%',
                            padding: '1rem',
                            borderRadius: '0.75rem',
                            background: 'rgba(255, 215, 0, 0.1)',
                            border: '1px solid rgba(255, 215, 0, 0.3)',
                            textAlign: 'center',
                            color: 'rgba(255, 215, 0, 0.8)',
                            fontSize: '0.875rem'
                        }}>
                            💡 {t('product.scanToOrder', 'Scan QR code at your table to order')}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

ProductModal.displayName = 'ProductModal';

export default ProductModal;
